// Global React Query (TanStack) client. The whole point of this layer:
//
//   • Navigations show cached data INSTANTLY, refetch in background.
//   • Two screens that need the same data make one network request, not two.
//   • Reloads after >5 min of idle still skip the spinner (data hydrated
//     from localStorage, refetched silently).
//
// Tuning notes:
//   • staleTime 30s — most lists in this app refresh on user action; this
//     is a comfortable balance for a snagging app where state mutations
//     come from the user themselves.
//   • gcTime 30min — keep data hot in memory long enough that the user
//     can wander out to take a photo and come back without losing it.
//   • refetchOnWindowFocus true — picks up changes from another device or
//     a teammate without forcing a manual reload.
//   • retry 1 — supabase REST should answer first try; one retry handles
//     transient network blips without making cold-start feel even slower.

import { QueryClient } from '@tanstack/react-query'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      gcTime: 30 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 0,
    },
  },
})

// Hydrate from localStorage so the next page-load shows the previous data
// while the network re-validates. This is the difference between "Loading…"
// and the page being there before the user can blink.
export const queryPersister = typeof window !== 'undefined'
  ? createSyncStoragePersister({
      storage: window.localStorage,
      key: 'ss.query-cache.v1',
      // Don't persist mutations or anything that includes 'signed-url' in
      // the key — signed storage URLs expire and would be stale.
      serialize: data => JSON.stringify(data),
      deserialize: text => JSON.parse(text),
    })
  : undefined
